

import React, { useEffect, useState } from "react";
import Breadcrumbs from "../components/Breadcrumbs";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  IconButton,
  Typography,
  Grid,
  Box,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { CheckCircle, MoreHoriz } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { fetchTasks } from "../redux/actions/taskActions";
import { fetchFiles } from "../redux/actions/fileActions";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import SelectWidgets from "../components/SelectWidgets";
import TimesheetCard from "../components/TimesheetCard";
import { listTimesheets } from "../redux/actions/timesheetActions";
import { Dialog, DialogContent, DialogTitle } from "@mui/material";

import {
  largeBold,
  mediumBold,
  mediumTitle,
  smallBold,
} from "../styles/typography";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import RecentCasesCard from "../components/RecentCasesCard";
import TasksOverview from "../components/TasksOverview";
import EventIcon from "@mui/icons-material/Event";
import GavelIcon from "@mui/icons-material/Gavel";
import { listAppointments } from "../redux/actions/appointmentActions";
import AgendaHomeCard from "../components/AgendaHomeCard";
import { useTranslation } from 'react-i18next';
import { formatDate } from "../utils/dateUtils";
const formatDateTask = (dateString) => formatDate(dateString);

const SixDots = () => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "repeat(3, auto)", // 3 columns for two rows
      gap: "4px", // Gap between the dots
      justifyContent: "center", // Center the grid horizontally
      alignItems: "center", // Center the grid vertically
    }}
  >
    {[...Array(6)].map((_, index) => (
      <Box
        key={index}
        sx={{
          width: "3px", // Adjust the size of the dots
          height: "3px",
          backgroundColor: "grey",
          borderRadius: "50%",
        }}
      />
    ))}
  </Box>
);

const HomePage = () => {
  const { t } = useTranslation(['pages', 'common']);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { tasks } = useSelector((state) => state.taskList);
  const { timesheets } = useSelector((state) => state.timesheetList);
  const { files } = useSelector((state) => state.fileList);
  const { user } = useSelector((state) => state.userLoaded); // Assuming user info is stored in auth state

  const { appointments } = useSelector((state) => state.appointmentList);
  const [activeTab, setActiveTab] = useState(0);
  const [cards, setCards] = useState([
    { id: "1", content: "recentCases" },
    { id: "2", content: "tasks" },
    { id: "3", content: "timesheets" }, // New card
    { id: "4", content: "appointments" }, // New card
  ]);
  const [toggleStates, setToggleStates] = useState({
    Tasks: true,
    RecentCases: true,
    timesheets: true,
    appointments: true,
  });

  const [draggingCard, setDraggingCard] = useState(null);

  useEffect(() => {
    // Only fetch if not already loaded to avoid unnecessary API calls
    if (!tasks || tasks.length === 0) {
      dispatch(fetchTasks());
    }
    if (!files || files.length === 0) {
      dispatch(fetchFiles());
    }
    // Appointments and timesheets might need to be refreshed for calendar view
    dispatch(listAppointments());
    dispatch(listTimesheets());
  }, [dispatch, tasks, files]);

  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleNavigate = (path) => {
    navigate(path);
    handleClose();
  };

  const filteredTasks = tasks
    ?.filter(
      (task) => task.owner?._id.toString() === user?.relationship.toString()
    )
    .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)) // Sort by date
    .slice(0, 100); // Increased from 30 to 100 for better user experience

  console.log(filteredTasks);

  const filteredAppointments = appointments
    ?.filter(
      (appointment) =>
        appointment.owner?._id.toString() === user?.relationship.toString()
    )
    .sort((a, b) => new Date(a.startDate) - new Date(b.startDate)) // Sort by date
    .slice(0, 100); // Increased from 30 to 100 for better user experience

  const userFiles = files
    ?.filter((file) => {
      // if (user.role === "Manager") {
      //   return true; // Managers can see all files
      // } else
      if (user?.role === "Manager" || user?.role === "Responsible Lawyer") {
        return file.officeMembers.some((member) => {
          return (
            member?.member?._id === user?.relationship &&
            member.role === "Responsible Lawyer"
          );
        });
      } else if (user?.role === "Assistant Lawyer") {
        return file.officeMembers.some(
          (member) =>
            member?.member._id === user?.relationship &&
            member?.role === "Treating Lawyer"
        );
      }
      return false;
    })
    .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)); // Sort by updatedAt in descending order

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const onDragStart = (start) => {
    console.log("Drag started for index:", start.source.index);
    setDraggingCard(start.source.index);
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return; // If dropped outside

    const updatedCards = Array.from(cards);
    const [movedCard] = updatedCards.splice(result.source.index, 1);
    updatedCards.splice(result.destination.index, 0, movedCard);

    setCards(updatedCards);
    setDraggingCard(null); // Reset dragging state after drag ends
  };

  return (
    <Box sx={{ padding: "0 24px 24px 24px", margin: "0 auto" }}>
      <div style={{ position: "relative" }}>
        {/* First Grid - Visible */}
        <DragDropContext onDragEnd={handleDragEnd} onDragStart={onDragStart}>
          <Droppable droppableId="cards-droppable" direction="horizontal">
            {(provided, snapshot) => (
              <Grid
                container
                spacing={4}
                {...provided.droppableProps}
                ref={provided.innerRef}
                sx={{ position: "relative", zIndex: 1 }}
              >
                {cards.map((card, index) => (
                  <Draggable key={card.id} draggableId={card.id} index={index}>
                    {(provided, snapshot) => (
                      <Grid
                        item
                        xs={12}
                        sm={4}
                        md={4}
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={{
                          ...provided.draggableProps.style,
                          transition: "transform 0.2s ease",
                          opacity: snapshot.isDragging ? 0.8 : 1,
                        }}
                      >
                        <Card
                          sx={{
                            height: "550px",
                            backgroundColor: "white",

                            boxShadow: "0px 4px 16px rgba(0,0,0,0.1)", // Reduced shadow spread
                            border: "none",
                            borderRadius: 0,
                            position: "relative",
                            display:
                              (card.content === "tasks" &&
                                !toggleStates.Tasks) ||
                              (card.content === "recentCases" &&
                                !toggleStates.RecentCases)
                                ? "none"
                                : "block",
                            "&:hover .six-dots": {
                              display: "flex",
                            },
                            cursor: "default",
                          }}
                        >
                          <Box
                            className="six-dots"
                            sx={{
                              display: "none", // Initially hidden
                              position: "absolute",
                              top: "5px",
                              right: "50%",
                              cursor: "grab",
                              zIndex: 12,
                            }}
                          >
                            <SixDots />
                          </Box>
                          <CardContent sx={{ paddingTop: 2 }}>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <Typography
                                sx={{
                                  color: "rgb(53, 53, 53)",
                                  display: "block",
                                  fontFamily:
                                    '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                                  fontSize: "12px",
                                  fontWeight: 600,
                                  height: "18px",
                                  lineHeight: "18px",
                                }}
                              >
                                {card.content === "recentCases"
                                  ? t('pages:dashboard.recent_files')
                                  : card.content === "tasks"
                                  ? t('common:tasks')
                                  : card.content === "appointments"
                                  ? t('pages:appointments.title')
                                  : t('common:timesheets')}
                              </Typography>
                              {card.content === "recentCases" ? (
                                <div style={{ display: "flex", gap: "8px" }}>
                                  <IconButton
                                    sx={{ padding: "5px" }}
                                    onClick={() => navigate(`/files/create`)}
                                  >
                                    <AddIcon
                                      sx={{
                                        fontSize: "24px",
                                        fontWeight: "400",
                                        color: "#2B80B6",
                                      }}
                                    />
                                  </IconButton>
                                  <IconButton
                                    sx={{ padding: "5px" }}
                                    onClick={() => navigate(`/files`)}
                                  >
                                    <ArrowUpwardIcon
                                      style={{
                                        fontSize: "24px",
                                        color: "#2B80B6",
                                        transform: "rotate(45deg)",
                                      }}
                                    />
                                  </IconButton>
                                </div>
                              ) : (
                                <div style={{ display: "flex", gap: "8px" }}>
                                  <IconButton
                                    sx={{ padding: "5px" }}
                                    onClick={() => {
                                      if (card.content === "appointments") {
                                        handleOpen();
                                      } else {
                                        navigate(`/${card.content}/create`);
                                      }
                                    }}
                                  >
                                    <AddIcon
                                      sx={{
                                        fontSize: "24px",
                                        color: "#2B80B6",
                                      }}
                                    />
                                  </IconButton>
                                  <IconButton
                                    sx={{ padding: "5px" }}
                                    onClick={() =>
                                      navigate(
                                        card.content === "appointments"
                                          ? "/calendar"
                                          : `/${card.content}`
                                      )
                                    }
                                  >
                                    <ArrowUpwardIcon
                                      style={{
                                        fontSize: "24px",
                                        color: "#2B80B6",
                                        transform: "rotate(45deg)",
                                      }}
                                    />
                                  </IconButton>
                                </div>
                              )}
                            </div>
                            {card.content === "recentCases" ? (
                              <RecentCasesCard userFiles={userFiles} />
                            ) : card.content === "tasks" ? (
                              <TasksOverview filteredTasks={filteredTasks} /> // Use the new component
                            ) : card.content === "timesheets" ? (
                              <TimesheetCard />
                            ) : (
                              <AgendaHomeCard
                                appointments={filteredAppointments}
                              />
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Grid>
            )}
          </Droppable>
        </DragDropContext>

        {/* Second Grid - Invisible (behind the first grid) */}
        <div
          style={{
            position: "absolute",
            top: "1rem",
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 0,
          }}
        >
          <Grid container spacing={2}>
            {cards.map((card, index) => (
              <Grid key={card.id} item xs={12} sm={4} md={4}>
                <Card
                  sx={{ height: "600px" }}
                  style={{
                    backgroundColor: "lightblue",
                    display: draggingCard === index ? "block" : "none",
                    border: "1px solid blue",
                  }}
                >
                  {console.log("the condition is", draggingCard === index)}
                </Card>
              </Grid>
            ))}
          </Grid>
        </div>
      </div>
      <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            borderRadius: 0,
          },
        }}
      >
        <DialogTitle>Add New</DialogTitle>
        <DialogContent
          sx={{ backgroundColor: "white", display: "flex", gap: 2 }}
        >
          <Card
            sx={{
              width: "50%",
              padding: "20px 50px",
              height: "80px",
              cursor: "pointer",
              borderRadius: 0,
              border: "1px solid lightgrey",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "none",
            }}
            onClick={() => handleNavigate("/appointment/create")}
          >
            <CardContent sx={{ textAlign: "center" }}>
              <EventIcon sx={{ fontSize: "36px", color: "#007ac3" }} />
              <Typography variant="h6">{t('common:appointment')}</Typography>
            </CardContent>
          </Card>
          <Card
            sx={{
              width: "50%",
              padding: "20px 50px",
              height: "80px",
              cursor: "pointer",
              borderRadius: 0,
              border: "1px solid lightgrey",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "none",
            }}
            onClick={() => handleNavigate("/hearing/create")}
          >
            <CardContent sx={{ textAlign: "center" }}>
              <GavelIcon sx={{ fontSize: "36px", color: "#007ac3" }} />
              <Typography variant="h6">{t('common:hearing')}</Typography>
            </CardContent>
          </Card>
        </DialogContent>
      </Dialog>
      {/* <SelectWidgets
        toggleStates={toggleStates}
        setToggleStates={setToggleStates}
      /> */}
    </Box>
  );
};

export default HomePage;